"use client";

import _slicedToArray from "/home/circleci/project/web-front/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import { useRadioContext } from "./MDCSFLUU.js";
import { useCompositeItem } from "./NSB34BGG.js";
import { createElement, createHook, forwardRef, memo } from "./7FZLUSKW.js";
import { useStoreState } from "./CTKPPFRS.js";
import { useEvent, useForceUpdate, useId, useMergeRefs, useTagName } from "./HNHAUIIY.js";
import { __objRest, __spreadProps, __spreadValues } from "./3YLGPPWQ.js";

// src/radio/radio.tsx
import { disabledFromProps, removeUndefinedValues } from "@ariakit/core/utils/misc";
import { useEffect, useRef } from "react";
var TagName = "input";
function getIsChecked(value, storeValue) {
  if (storeValue === void 0) return;
  if (value != null && storeValue != null) {
    return storeValue === value;
  }
  return !!storeValue;
}
function isNativeRadio(tagName, type) {
  return tagName === "input" && (!type || type === "radio");
}
var useRadio = createHook(function useRadio2(_a) {
  var _b = _a,
    store = _b.store,
    name = _b.name,
    value = _b.value,
    checked = _b.checked,
    props = __objRest(_b, ["store", "name", "value", "checked"]);
  var context = useRadioContext();
  store = store || context;
  var id = useId(props.id);
  var ref = useRef(null);
  var isChecked = useStoreState(store, function (state) {
    return checked != null ? checked : getIsChecked(value, state == null ? void 0 : state.value);
  });
  useEffect(function () {
    if (!id) return;
    if (!isChecked) return;
    var isActiveItem = (store == null ? void 0 : store.getState().activeId) === id;
    if (isActiveItem) return;
    store == null ? void 0 : store.setActiveId(id);
  }, [store, isChecked, id]);
  var onChangeProp = props.onChange;
  var tagName = useTagName(ref, TagName);
  var nativeRadio = isNativeRadio(tagName, props.type);
  var disabled = disabledFromProps(props);
  var _useForceUpdate = useForceUpdate(),
    _useForceUpdate2 = _slicedToArray(_useForceUpdate, 2),
    propertyUpdated = _useForceUpdate2[0],
    schedulePropertyUpdate = _useForceUpdate2[1];
  useEffect(function () {
    var element = ref.current;
    if (!element) return;
    if (nativeRadio) return;
    if (isChecked !== void 0) {
      element.checked = isChecked;
    }
    if (name !== void 0) {
      element.name = name;
    }
    if (value !== void 0) {
      element.value = "".concat(value);
    }
  }, [propertyUpdated, nativeRadio, isChecked, name, value]);
  var onChange = useEvent(function (event) {
    if (disabled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if ((store == null ? void 0 : store.getState().value) === value) return;
    if (!nativeRadio) {
      event.currentTarget.checked = true;
      schedulePropertyUpdate();
    }
    onChangeProp == null ? void 0 : onChangeProp(event);
    if (event.defaultPrevented) return;
    store == null ? void 0 : store.setValue(value);
  });
  var onClickProp = props.onClick;
  var onClick = useEvent(function (event) {
    onClickProp == null ? void 0 : onClickProp(event);
    if (event.defaultPrevented) return;
    if (nativeRadio) return;
    onChange(event);
  });
  var onFocusProp = props.onFocus;
  var onFocus = useEvent(function (event) {
    onFocusProp == null ? void 0 : onFocusProp(event);
    if (event.defaultPrevented) return;
    if (!nativeRadio) return;
    if (!store) return;
    var _store$getState = store.getState(),
      moves = _store$getState.moves,
      activeId = _store$getState.activeId;
    if (!moves) return;
    if (id && activeId !== id) return;
    onChange(event);
  });
  props = __spreadProps(__spreadValues({
    id: id,
    role: !nativeRadio ? "radio" : void 0,
    type: nativeRadio ? "radio" : void 0,
    "aria-checked": isChecked
  }, props), {
    ref: useMergeRefs(ref, props.ref),
    onChange: onChange,
    onClick: onClick,
    onFocus: onFocus
  });
  props = useCompositeItem(__spreadValues({
    store: store,
    clickOnEnter: !nativeRadio
  }, props));
  return removeUndefinedValues(__spreadValues({
    name: nativeRadio ? name : void 0,
    value: nativeRadio ? value : void 0,
    checked: isChecked
  }, props));
});
var Radio = memo(forwardRef(function Radio2(props) {
  var htmlProps = useRadio(props);
  return createElement(TagName, htmlProps);
}));
export { useRadio, Radio };